import Markdown from "markdown-to-jsx";
import { AnchorHTMLAttributes, useMemo } from "react";
import { Controller, GlobalError, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Checkbox, Text } from "chameleon/lib/components";

import {
  MandateConfig,
  PaymentType,
} from "checkout-query-client/types/form-types";

import { MandateVerbiage, PlainMessage } from "frontend-client";

import { usePaymentOptionContext } from "../payment-method/payment-option-provider";

type MandateProps = {
  mandate: MandateConfig;
  mandateType: PaymentType | "placeOrder";
  index: number;
};

const LinkOpeningInNewWindow = ({
  children,
  ...props
}: AnchorHTMLAttributes<HTMLAnchorElement>) => {
  return (
    <a {...props} target="_blank">
      {children}
    </a>
  );
};

export const Mandate = ({ mandate, mandateType, index }: MandateProps) => {
  const { t, i18n } = useTranslation(["combined"]);
  const { control, formState } = useFormContext();
  const { paymentOption } = usePaymentOptionContext() ?? {};
  const { formId = "" } = paymentOption || {};
  const fieldId = `mandate-${mandate.mandateId}`;

  const isRequired = useMemo(
    () => mandate.showAcknowledgement && mandate.acknowledgementRequired,
    [mandate]
  );

  const getVerbiage = (verbiageList: Array<PlainMessage<MandateVerbiage>>) => {
    const language = i18n.language ?? "en-US";
    let text = verbiageList?.find((v) => v.locale === language)?.verbiage || "";
    if (!text) {
      text = verbiageList?.find((v) => v.locale === "en-US")?.verbiage || "";
    }

    // INFO: We are left-trimming the text because as per Markdown rules
    // 4 or more spaces transforms into a code block
    return text.trimStart();
  };

  const content = (
    <Text
      testId="mandate-verbiage"
      styleVariant={mandate.showAcknowledgement ? "body" : "caption"}
      colorVariant={
        mandate.showAcknowledgement ? "primaryText" : "secondaryText"
      }
    >
      <Markdown
        options={{
          overrides: {
            a: {
              component: LinkOpeningInNewWindow,
            },
          },
        }}
      >
        {getVerbiage(mandate.mandateVerbiages)}
      </Markdown>
    </Text>
  );

  const errorType = (formState.errors[formId] as Record<string, GlobalError>)?.[
    fieldId
  ]?.type as string;
  const errorMessage = useMemo(() => {
    return errorType ? t(`checkoutForm.mandate.errors.${errorType}`) : "";
  }, [errorType]);

  return (
    <>
      {mandate.showAcknowledgement ? (
        <Controller
          name={`${formId}.${fieldId}`}
          rules={{
            validate: (value) =>
              !isRequired || (isRequired && value !== "false"),
          }}
          defaultValue={"false"}
          control={control}
          render={({ field }) => (
            <Checkbox
              id={mandate.mandateId}
              isRequired={isRequired}
              testId={`mandate-checkbox-${mandateType}-${index}`}
              content={content}
              hasError={!!errorMessage}
              errorMessage={errorMessage}
              errorMessageKey={errorType}
              isChecked={field.value === "true"}
              {...field}
              onChange={(e) => {
                field.onChange(`${e.target.checked}`);
                field.onBlur();
              }}
            />
          )}
        />
      ) : (
        content
      )}
    </>
  );
};
